<template>
  <div class="page-container">
    <div class="settings-form">
      <h2 class="page-title">写作模型管理</h2>

      <!-- 搜索区域 -->
      <div class="search-bar">
        <el-input v-model="searchForm.writing_name" placeholder="请输入接口名称" clearable size="large"
          style="width: 300px; margin-right: 15px" @keyup.enter="handleSearch">
          <template #prefix>
            <el-icon>
              <Search />
            </el-icon>
          </template>
        </el-input>

        <el-button type="primary" size="large" @click="handleSearch">
          <el-icon>
            <Search />
          </el-icon>
          搜索
        </el-button>

        <el-button type="success" size="large" @click="openAddDialog" style="float: right">
          <el-icon>
            <Plus />
          </el-icon>
          新增模型
        </el-button>
      </div>

      <!-- 模型列表 -->
      <el-table :data="modelList" stripe style="width: 100%; margin-top: 20px" v-loading="loading">
        <el-table-column prop="writing_name" label="接口名称" width="180" />
        <el-table-column prop="writing_type_text" label="写作类型" width="120" />
        <el-table-column prop="key_preview" label="密钥预览" width="180">
          <template #default="{ row }">
            <span class="key-preview">{{ row.key_preview }}******</span>
          </template>
        </el-table-column>
        <el-table-column prop="writing_url" label="接口地址" min-width="200" show-overflow-tooltip />
        <el-table-column prop="writing_model" label="模型名称" width="150" />
        <el-table-column prop="notes" label="备注" width="150" />
        <el-table-column prop="create_time" label="创建时间" width="180" />
        <el-table-column prop="update_time" label="更新时间" width="180" />

        <el-table-column label="操作" width="200" fixed="right">
          <template #default="scope">
            <el-button type="primary" size="small" @click="openEditDialog(scope.row)">
              <el-icon>
                <Edit />
              </el-icon>
              编辑
            </el-button>
            <el-button type="danger" size="small" @click="handleDelete(scope.row.id)">
              <el-icon>
                <Delete />
              </el-icon>
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页组件 -->
      <div class="pagination-container">
        <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
          :total="pagination.total" :page-sizes="[10, 20, 30, 50]" layout="total, sizes, prev, pager, next, jumper"
          @size-change="fetchModelList" @current-change="fetchModelList" />
      </div>
    </div>

    <!-- 新增/编辑对话框 -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="600px" :close-on-click-modal="false">
      <el-form ref="modelForm" :model="modelForm" label-width="120px" :rules="formRules">
        <el-form-item label="接口名称" prop="writing_name">
          <el-input v-model="modelForm.writing_name" placeholder="请输入接口名称" size="large" />
        </el-form-item>

        <el-form-item label="写作类型" prop="writing_type">
          <el-select v-model="modelForm.writing_type" placeholder="请选择写作类型" size="large">
            <el-option v-for="type in writingTypes" :key="type.value" :label="type.label" :value="type.value" />
          </el-select>
        </el-form-item>

        <!-- 新增：预设模型选择 -->
        <el-form-item label="预设模型">
          <el-select v-model="selectedPresetModel" placeholder="请选择预设模型" size="large" @change="handlePresetModelChange"
            clearable>
            <el-option v-for="model in presetModels" :key="model.value" :label="model.label" :value="model" />
          </el-select>
        </el-form-item>

        <el-form-item label="接口地址" prop="writing_url">
          <el-input v-model="modelForm.writing_url" placeholder="请输入写作接口地址" size="large" />
        </el-form-item>

        <el-form-item label="接口密钥" prop="writing_key">
          <el-input v-model="modelForm.writing_key" placeholder="请输入接口密钥" size="large" show-password />
        </el-form-item>

        <el-form-item label="模型名称" prop="writing_model">
          <el-input v-model="modelForm.writing_model" placeholder="请输入模型名称" size="large" />
        </el-form-item>

        <!-- 新增：测试连通性按钮 -->
        <el-form-item label="测试连通性">
          <el-button type="primary" size="large" @click="testConnection" :loading="testingConnection"
            :disabled="!modelForm.writing_url || !modelForm.writing_key || !modelForm.writing_model">
            <el-icon>
              <Connection />
            </el-icon>
            测试连通性
          </el-button>
          <span class="test-result" :class="{ 'success': testResult === 'success', 'error': testResult === 'error' }">
            {{ testResult === 'success' ? '✓ 测试通过' : testResult === 'error' ? '✗ 测试失败' : '' }}
          </span>
        </el-form-item>

        <el-form-item label="备注信息">
          <el-input v-model="modelForm.notes" placeholder="请输入备注信息" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitModelForm">确认</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import {
  Search, Plus, Edit, Delete, Connection
} from '@element-plus/icons-vue'

export default {
  name: 'SystemModel',
  components: { Search, Plus, Edit, Delete, Connection },
  data() {
    // URL验证函数
    const validateUrl = (rule, value, callback) => {
      if (!value) {
        callback(new Error('请输入接口地址'));
      } else if (!/^https?:\/\/.+/.test(value)) {
        callback(new Error('请输入有效的URL地址'));
      } else {
        callback();
      }
    };

    return {
      loading: false,
      dialogVisible: false,
      dialogTitle: '新增模型',
      isEditMode: false,
      testingConnection: false,
      testResult: '',
      searchForm: {
        writing_name: ''
      },

      // 预设模型列表
      presetModels: [
        { label: '智谱大模型', value: 'glm-4-flash', url: 'https://open.bigmodel.cn/api/paas/v4/chat/completions', modelName: 'glm-4-flash' },
        { label: '阿里通义千问', value: 'qwen-max', url: 'https://dashscope.aliyuncs.com/compatible-mode/v1/chat/completions', modelName: 'qwen-max' },
        { label: '百度千帆大模型', value: 'ernie-4.0-8k', url: 'https://qianfan.baidubce.com/v2/chat/completions', modelName: 'ernie-4.0-8k' },
        { label: '腾讯混元大模型', value: 'hunyuan-turbo-latest', url: 'https://api.hunyuan.cloud.tencent.com/v1/chat/completions', modelName: 'hunyuan-turbo-latest' },
        { label: 'DeepSeek深度求索', value: 'deepseek-chat', url: 'https://api.deepseek.com/chat/completions', modelName: 'deepseek-chat' },
        { label: '火山方舟豆包/DeepSeek满血版', value: 'glm-4-flash-volc', url: 'https://ark.cn-beijing.volces.com/api/v3/chat/completions', modelName: 'glm-4-flash' },
        { label: 'Kimi月之暗面', value: 'moonshot-v1-128k', url: 'https://api.moonshot.cn/v1/chat/completions', modelName: 'moonshot-v1-128k' },
        { label: '自定义大模型', value: 'custom', url: '', modelName: '' }
      ],
      selectedPresetModel: null, // 当前选中的预设模型

      writingTypes: [
        { label: '文章创作', value: '1' },
        { label: '标题创作', value: '2' }
      ],
      modelForm: {
        id: null,
        writing_name: '',
        writing_type: '1',
        writing_url: '',
        writing_key: '',
        writing_model: '',
        notes: ''
      },
      formRules: {
        writing_name: [
          { required: true, message: '请输入接口名称', trigger: 'blur' }
        ],
        writing_type: [
          { required: true, message: '请选择写作类型', trigger: 'change' }
        ],
        writing_url: [
          { required: true, validator: validateUrl, trigger: 'blur' }
        ],
        writing_key: [
          { required: true, message: '请输入接口密钥', trigger: 'blur' }
        ],
        writing_model: [
          { required: true, message: '请输入模型名称', trigger: 'blur' }
        ]
      },
      modelList: [],
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      }
    }
  },
  computed: {
    userInfo() {
      return JSON.parse(Cookies.get('user_info') || '{}')
    }
  },
  mounted() {
    this.fetchModelList();
  },
  methods: {

    // 测试模型连通性
    async testConnection() {
      if (!this.modelForm.writing_url || !this.modelForm.writing_key || !this.modelForm.writing_model) {
        this.$message.warning('请先填写接口地址、密钥和模型名称');
        return;
      }

      this.testingConnection = true;
      this.testResult = '';

      try {
        const response = await axios.post(
          this.modelForm.writing_url,
          {
            model: this.modelForm.writing_model,
            messages: [
              { "role": "system", "content": "You are a helpful assistant." },
              { "role": "user", "content": "Hello!" }
            ],
            stream: false
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${this.modelForm.writing_key}`
            },
            timeout: 10000 // 10秒超时
          }
        );

        if (response.data && response.data.choices) {
          this.testResult = 'success';
          this.$message.success('模型连接测试成功！');
        } else {
          this.testResult = 'error';
          this.$message.error('接口返回格式不符合预期');
        }
      } catch (error) {
        this.testResult = 'error';
        let errorMessage = '连接测试失败';

        if (error.response) {
          // 服务器响应了但状态码不在 2xx 范围内
          errorMessage += ` (状态码: ${error.response.status})`;
          if (error.response.data && error.response.data.error) {
            errorMessage += `: ${error.response.data.error.message || error.response.data.error}`;
          }
        } else if (error.request) {
          // 请求已发出但没有收到响应
          errorMessage += ': 服务器无响应';
        } else {
          // 请求设置出错
          errorMessage += `: ${error.message}`;
        }

        this.$message.error(errorMessage);
        console.error('连接测试错误:', error);
      } finally {
        this.testingConnection = false;
      }
    },
    // 处理预设模型选择
    handlePresetModelChange(model) {
      if (model && model.value !== 'custom') {
        this.modelForm.writing_url = model.url;
        this.modelForm.writing_model = model.modelName;
      } else if (model && model.value === 'custom') {
        // 选择自定义时清空字段
        this.modelForm.writing_url = '';
        this.modelForm.writing_model = '';
      }
    },

    // 获取模型列表
    async fetchModelList() {
      this.loading = true;
      try {
        const params = {
          action: this.searchForm.writing_name ? 'search' : 'list',
          store_id: this.userInfo.id,
          page: this.pagination.page,
          page_size: this.pagination.pageSize
        };

        // 如果有搜索条件，加入搜索参数
        if (this.searchForm.writing_name) {
          params.writing_name = this.searchForm.writing_name;
        }

        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/WritingModelKeyController.php`,
          params
        );

        if (data.data) {
          this.modelList = data.data;
          this.pagination.total = data.pagination?.total || data.data.length;
        }
      } catch (error) {
        this.$message.error('获取模型列表失败');
        console.error('获取模型列表错误:', error);
      } finally {
        this.loading = false;
      }
    },

    // 处理搜索
    handleSearch() {
      this.pagination.page = 1;
      this.fetchModelList();
    },

    // 打开新增对话框
    openAddDialog() {
      this.dialogTitle = '新增模型';
      this.isEditMode = false;
      this.testResult = '';
      this.modelForm = {
        id: null,
        writing_name: '',
        writing_type: '1',
        writing_url: '',
        writing_key: '',
        writing_model: '',
        notes: ''
      };
      this.selectedPresetModel = null; // 重置预设模型选择
      this.dialogVisible = true;
    },

    // 打开编辑对话框
    openEditDialog(row) {
      this.dialogTitle = '编辑模型';
      this.isEditMode = true;
      this.testResult = '';
      this.modelForm = {
        id: row.id,
        writing_name: row.writing_name,
        writing_type: row.writing_type,
        writing_url: row.writing_url,
        writing_key: row.writing_key,
        writing_model: row.writing_model,
        notes: row.notes
      };

      // 尝试匹配预设模型
      const matchedModel = this.presetModels.find(model =>
        model.url === row.writing_url && model.modelName === row.writing_model
      );

      this.selectedPresetModel = matchedModel || null;
      this.dialogVisible = true;
    },

    // 提交表单（新增/编辑）
    submitModelForm() {
      this.$refs.modelForm.validate(async (valid) => {
        if (valid) {
          try {
            const action = this.isEditMode ? 'update' : 'add';
            const payload = {
              ...this.modelForm,
              store_id: this.userInfo.id
            };

            const { data } = await axios.post(
              `${config.BASE_URL}/shop/api/WritingModelKeyController.php`,
              {
                action,
                ...payload
              }
            );

            if (data.success) {
              this.$message.success(this.isEditMode ? '更新成功' : '添加成功');
              this.dialogVisible = false;
              this.fetchModelList();
            } else {
              this.$message.error(data.error || '操作失败');
            }
          } catch (error) {
            const errorMsg = error.response?.data?.error || error.message || '操作失败';
            this.$message.error(`操作失败: ${errorMsg}`);
            console.error('表单提交错误:', error);
          }
        }
      });
    },

    // 删除模型
    async handleDelete(id) {
      this.$confirm('确定要删除该模型配置吗？', '警告', {
        confirmButtonText: '确认删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const { data } = await axios.post(
            `${config.BASE_URL}/shop/api/WritingModelKeyController.php`,
            {
              action: 'delete',
              id: id,
              store_id: this.userInfo.id
            }
          );

          if (data.success) {
            this.$message.success('删除成功');
            this.fetchModelList();
          } else {
            this.$message.error(data.error || '删除失败');
          }
        } catch (error) {
          const errorMsg = error.response?.data?.error || error.message || '删除失败';
          this.$message.error(`删除失败: ${errorMsg}`);
          console.error('删除错误:', error);
        }
      }).catch(() => { });
    }
  }
}
</script>

<style scoped lang="scss">
@use '@/assets/styles/page-common.scss' as *;

/* 新增特定样式 */
.key-preview {
  font-family: monospace;
  background-color: #f5f7fa;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 13px;
}

/* 表单验证提示样式 */
::deep(.el-form-item__error) {
  font-size: 12px;
  padding-top: 4px;
}

/* 新增测试结果样式 */
.test-result {
  margin-left: 15px;
  font-size: 14px;

  &.success {
    color: #67c23a;
  }

  &.error {
    color: #f56c6c;
  }
}
</style>